import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from 'src/components/base/outline-element/outline-element';
import componentStyles from './idb-media-card.css.lit';
import { ButtonVariant } from '../idb-button/idb-button';

/**
 * The IDB media Card component.
 * @element idb-media-card
 * @extends OutlineElement
 * @slot summary - The summary slot.
 * @slot media - The media slot.
 */

type MediaPositions = 'left' | 'right';

@customElement('idb-media-card')
export class IdbMediaCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'heading' })
  heading: string;

  @property({ type: String, attribute: 'subheading' })
  subheading: string;

  @property({ type: String, attribute: 'eyebrow' })
  eyebrow: string;

  @property({ type: String, attribute: 'button-variant' })
  buttonVariant: ButtonVariant = 'primary';

  @property({ type: String, attribute: 'button-label' })
  buttonLabel: string;

  @property({ type: String, attribute: 'button-url' })
  ButtonUrl: string;

  @property({ type: String, attribute: 'button-target' })
  buttonTarget: string;

  @property({ type: String, attribute: 'media-position' })
  mediaPosition: MediaPositions = 'left';

  @property({ type: Boolean, attribute: 'image-rounded' })
  imageRounded: true;

  @property({ type: String, attribute: 'theme' })
  theme: string;

  @property({ type: Boolean, attribute: 'play-in-place' })
  playInPlace: false;

  @property({ type: String, attribute: 'media-url' })
  mediaUrl: string;

  render(): TemplateResult {
    const cardClasses = {
      'media-card': true,
      [`theme--${this.theme}`]: this.theme,
      [`media-position--${this.mediaPosition}`]: this.mediaPosition,
    };
    return html`
      <div class=${classMap(cardClasses)}>
        ${this.mediaTemplate()}
        <div class="media-card__content">
          <div class="media-card__heading">
            ${this.eyebrowTemplate()}
            ${this.headingTemplate()}
            ${this.subheadingTemplate()}
          </div>
          <div class="media-card__summary">
            <slot name="summary"></slot>
          </div>
          ${
            this.ButtonUrl
              ? html`
            <div class="media-card__button">
              <idb-button slot="cta" 
                button-url="${this.ButtonUrl}" 
                button-variant="${this.buttonVariant}" 
                button-target="${this.buttonTarget}">
                ${this.buttonLabel}
              </idb-button>
            </div>
          `
              : null
          }
      </div>
      `;
  }

  /**
   * Returns a TemplateResult for the media card's media section.
   * @returns {TemplateResult} The media section's TemplateResult.
   */
  mediaTemplate(): TemplateResult {
    const mediaClasses = {
      'media-card__media': true,
      'media-card__media--rounded': this.imageRounded,
      'play-in-place': this.playInPlace,
      'open-in-new-tab': !this.playInPlace,
    };
    return html`
      <div class=${classMap(mediaClasses)}>
        ${
          this.playInPlace
            ? html`
          <slot name="media"></slot>
        `
            : html`
          <a href="${this.mediaUrl}" target="_blank">
            <idb-icon icon="play-circle" stroke-width="2"></idb-icon>
            <span class="overlay"></span>
            <slot name="media"></slot>
          </a>
        `
        }
        
      </div>
    `;
  }

  /**
   * Template partial for the Eyebrow slot.
   * @returns TemplateResult | null
   */
  eyebrowTemplate(): TemplateResult | null {
    if (!this.eyebrow) return null;
    return html`<idb-heading level="p" level-size="sm" level-style="semibold" capital-style="uppercase">
      ${this.eyebrow}
    </idb-heading>`;
  }

  /**
   * Template partial for the Title slot.
   * @returns TemplateResult | null
   */
  headingTemplate(): TemplateResult | null {
    if (!this.heading) return null;
    return html`
    <idb-heading level-size="h1-small" level-style="semibold" margin-size="0px">
      ${this.heading}
    </idb-heading>`;
  }

  /**
   * Template partial for the Title slot.
   * @returns TemplateResult | null
   */
  subheadingTemplate(): TemplateResult | null {
    if (!this.subheading) return null;
    return html`
    <idb-heading accent-color="" level="h3" level-style="semibold">
      ${this.subheading}
    </idb-heading>`;
  }
}
